import { useCallback } from 'react';
import useGlobalApiFacade from '../../apis/facades/useGlobalApiFacade';
import { getRollupsListForUi } from '../../components/pages/rollups/rollupHelpers';
import {
  setGlobalError,
  setGlobalData,
  setGlobalIsLoading,
  selectEnvironments,
} from '../../reducers/global/globalReducer';
import { GlobalData } from '../../reducers/global/globalReducerTypes';
import { useDispatch, useSelector } from 'react-redux';
import { transformResponse } from '../../components/parser/parserUtils';
import { DeployStatusForTable } from '../../types/enums/DeployStatusForTable';
import axios from 'axios';
import {
  selectCrmOrgs,
  setIsLoadingObjectTypes,
  setAndSortObjectTypes,
} from '../../components/pages/environments/environmentsReducer';
import { isOrgConnectionError } from './environmentsHelper';
import isEqual from 'lodash/isEqual';
import { telemetry } from '../../telemetry';
import {
  generateTagsAndUsersSummary,
  setParserItemsMap,
} from '../../reducers/documentation/userInputsReducer';
import { createDocumentationItemsMap } from '../../reducers/documentation/createDocumentationItemsMap';

// Shared across all useFetchGlobal instances.
// Tracks the latest fetch generation per crmOrgId so that when a newer fetch is
// initiated (from any hook instance), older in-flight responses are discarded.
const fetchGenerationPerOrg: Record<string, number> = {};

// Call this when an optimistic update (addAutomation, updateAutomation, etc.) is
// dispatched. It invalidates any in-flight fetchGlobal for that org, preventing
// stale API responses from overwriting the optimistic state.
export function invalidateFetchGeneration(crmOrgId: string) {
  fetchGenerationPerOrg[crmOrgId] = (fetchGenerationPerOrg[crmOrgId] || 0) + 1;
}

type AutomationsMap = { [automationId: string]: AutomationStructureNew };

const getAutomationsListForUi = (automations?: AutomationStructureNew[]) => {
  const automationsMap: AutomationsMap | undefined = automations?.reduce(
    (res: AutomationsMap, automation) => {
      if (res[automation.automationId]) {
        //If the automation already exist in the map, it means this automation should be presented with status "Pending"
        //the newItem should get the versionId from the "Draft"
        let newItem;
        if (res[automation.automationId].status === 'Deployed') {
          newItem = {
            ...automation,
            deployedVersionId: res[automation.automationId].versionId,
            status: DeployStatusForTable.Pending,
            deployedAs: res[automation.automationId].deployedAs,
          };
        } else {
          newItem = {
            ...res[automation.automationId],
            deployedVersionId: automation.versionId,
            status: DeployStatusForTable.Pending,
            deployedAs: automation.deployedAs,
          };
        }
        res[automation.automationId] = newItem;
      } else {
        res[automation.automationId] = automation;
      }
      return res;
    },
    {},
  );
  return automationsMap ? Object.values(automationsMap) : undefined;
};

export const useFetchGlobal = () => {
  const dispatch = useDispatch();
  const { get_global } = useGlobalApiFacade();
  const crmOrgs = useSelector(selectCrmOrgs, isEqual);
  const environments = useSelector(selectEnvironments);

  const fetchGlobal = useCallback(
    async ({
      crmOrgId,
      skipLoadingObjectTypes,
      skipIfAlreadyLoaded,
    }: {
      crmOrgId: string;
      skipLoadingObjectTypes?: boolean;
      skipIfAlreadyLoaded?: boolean;
    }) => {
      if (skipIfAlreadyLoaded) {
        const environmentIsAlreadyLoaded = !!environments[crmOrgId];
        if (environmentIsAlreadyLoaded) return;
      }

      const orgIdx = crmOrgs.findIndex((crmOrg) => crmOrg.id === crmOrgId);
      if (orgIdx < 0) return;

      const orgFetchStatus = crmOrgs[orgIdx].fetchStatus;
      const isConnectionError = isOrgConnectionError(orgFetchStatus);
      if (isConnectionError) return;

      // Track the generation of this fetch. If a newer fetch is initiated while
      // this one is in-flight (from any useFetchGlobal instance), we discard
      // this stale response to preserve optimistic updates in Redux.
      const generation = (fetchGenerationPerOrg[crmOrgId] || 0) + 1;
      fetchGenerationPerOrg[crmOrgId] = generation;

      dispatch(setGlobalIsLoading({ crmOrgId }));
      !skipLoadingObjectTypes && dispatch(setIsLoadingObjectTypes({ crmOrgId }));
      try {
        const data = await get_global(crmOrgId);

        // If a newer fetch was started while we were waiting, discard this
        // stale response — the newer fetch will provide fresher data.
        // Note: we intentionally do NOT clear isLoading here. A newer fetch
        // (or the one triggered by the WebSocket event following the optimistic
        // update) is already in-flight and will clear it when it completes.
        // Clearing it prematurely would cause a brief "loaded" flash while
        // the newer fetch is still pending.
        if (fetchGenerationPerOrg[crmOrgId] !== generation) {
          return;
        }

        const {
          rollups,
          assignments,
          automations,
          alerts,
          dedup,
          matching,
          scheduledAssignments,
          scheduledReport,
          objectTypes,
          marketingAttribution,
          ...rest
        } = data;
        const globalData: GlobalData = {
          ...rest,
          rollups: getRollupsListForUi(rollups),
          assignments: getAutomationsListForUi(assignments),
          automations: getAutomationsListForUi(automations),
          documentation: transformResponse(data),
          alerts: getAutomationsListForUi(alerts),
          dedup: getAutomationsListForUi(dedup),
          matching: getAutomationsListForUi(matching),
          scheduledAssignments: getAutomationsListForUi(scheduledAssignments),
          scheduledReport: getAutomationsListForUi(scheduledReport),
          marketingAttribution: getAutomationsListForUi(marketingAttribution),
        };

        const parserItemsMap = createDocumentationItemsMap(data);

        dispatch(
          setGlobalData({
            crmOrgId,
            globalData,
          }),
        );
        dispatch(setAndSortObjectTypes({ crmOrgId, objectTypes }));

        //needed for tags center
        dispatch(setParserItemsMap({ parserItemsMap, crmOrgId }));
        dispatch(generateTagsAndUsersSummary({ crmOrgId }));
      } catch (error) {
        telemetry.captureError(error);
        if (axios.isAxiosError(error)) {
          dispatch(setGlobalError({ crmOrgId, error: error.response?.data }));
        }
      }
    },
    // "environments" intentionally excluded — it changes on every setGlobalData which
    // would re-create fetchGlobal, re-trigger the listener effect, and cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, get_global, crmOrgs],
  );

  return { fetchGlobal };
};
